/**
 * Onboarding wizard step definitions (Phase 2, decision O8 → logical clusters).
 *
 * The wizard is an alternate *renderer* over the same question config and data
 * model as the Phase 1 form — these clusters only group the existing question
 * keys into steps. A question hidden by display rules is simply skipped within
 * its step; every cluster keeps at least one always-visible question so no
 * step can render empty.
 */

export interface OnboardingStep {
  id: string;
  title: string;
  /** Short line shown under the step title. */
  blurb: string;
  /** Question keys (from the server config) rendered on this step, in order. */
  questionKeys: string[];
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'segments',
    title: 'Market',
    blurb: 'The kinds of deals you invest in.',
    // `utility_revenue_types` is hidden by display rules unless UTILITY is
    // among the selected market segments — kept in this cluster so it lives
    // next to the question that triggers it.
    questionKeys: ['market_segment', 'utility_revenue_types'],
  },
  {
    id: 'deal',
    title: 'Deal preferences',
    blurb: 'Stage, structure, and size of deals you want to see.',
    questionKeys: [
      'development_stage',
      // Hidden by display rules unless the parent has DEVELOPMENT selected.
      'development_substage',
      'deal_structure',
      // Hidden by display rules unless the parent has ASSET_MA selected.
      'asset_ma_type',
      'deal_size',
      'deal_timing',
    ],
  },
  {
    id: 'tech_geo',
    title: 'Technology & geography',
    blurb: 'Technologies, locations, and customer credit you target.',
    questionKeys: [
      'technology',
      // Per-project size windows, split by technology — hidden by display rules
      // unless the matching technology (PV / BESS) is selected above. Kept next
      // to `technology` so they appear right after the answer that gates them.
      'project_size_pv',
      'project_size_bess',
      'mounting_type',
      'geography',
      'utility_zones',
      'customer_credit',
    ],
  },
  {
    id: 'tax',
    title: 'Tax credits',
    blurb: 'ITC eligibility, adders, and safe-harbor preferences.',
    questionKeys: [
      'itc_eligible',
      'itc_adders',
      'itc_safe_harbor_status',
      // Shown by display rules based on the safe-harbor status selections.
      'itc_safe_harbor_seller_strategy',
      'itc_safe_harbor_seller_soc_year',
      'itc_buyer_no_safe_harbor_strategy',
      // Shown by display rules only when "I can bring safe harbored equipment"
      // is selected above.
      'itc_buyer_equipment_notes',
    ],
  },
  {
    id: 'notes',
    title: 'Anything else',
    blurb: 'Free-form notes for anything the questions did not cover.',
    questionKeys: ['notes'],
  },
];
